import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from '@entur/form';
import { CheckIcon } from '@entur/icons';
import { Tooltip } from '@entur/tooltip';
import { sprintf, vsprintf } from 'sprintf';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import CopyToClipboard from 'components/CopyToClipboard';
import { formatAmountCurrency, replaceDotsWithCommas } from 'utils/numberUtils';
import { LegacyColumnConfig, LinkConfig } from '../types';
import styles from './parseCellValue.module.scss';

dayjs.extend(duration);

const TIME_FORMAT_SECONDS = 'seconds';
const TIME_FORMAT_MINUTES = 'minutes';
const TIME_FORMAT_HOURS = 'hours';

export const parseLink = (link: LinkConfig | undefined, cellValue: unknown, row: unknown): string | null => {
  if (!link) return null;
  const { url, params } = link;
  if (!params) return sprintf(url, cellValue);
  const { list, columns } = params;
  const columnParams = columns ? columns.map((c) => (row as Record<string, unknown>)[c]) : [];
  // Spread, so each column fills its own %n$s; nested, every column landed in %1$s and %2$s was undefined.
  const paramsList = [...(list ? [list] : []), ...columnParams];
  return vsprintf(url, paramsList);
};

export const sortByIndex = (a: [string, { index?: number }], b: [string, { index?: number }]): number =>
  (a[1].index ?? 0) - (b[1].index ?? 0);

export const parseCellValue = (cellValue: unknown, config: LegacyColumnConfig, row: unknown): ReactNode => {
  const {
    amount,
    decimals = 2,
    format,
    timeFormat,
    icon,
    textKey,
    checkbox,
    parse,
    parseObject,
    number,
    ref: isRef,
  } = config;

  if (amount) return formatAmountCurrency(cellValue as string | number | null, { decimals });

  if (format) {
    if (!cellValue) return '';
    return dayjs(cellValue as string).format(format);
  }

  if (parse) return parse(cellValue);

  if (parseObject) {
    return row ? parseObject(row) : '';
  }

  if (number) return replaceDotsWithCommas(cellValue as string | number | undefined);

  if (timeFormat) {
    const time = dayjs.duration(cellValue as number, 'milliseconds');
    switch (timeFormat) {
      case TIME_FORMAT_MINUTES:
        return `${time.asMinutes()}m`;
      case TIME_FORMAT_HOURS:
        return `${time.asHours()}t`;
      case TIME_FORMAT_SECONDS:
      default:
        return `${time.asSeconds()}s`;
    }
  }

  if (icon && typeof icon !== 'boolean') {
    const Icon = icon.component;
    return cellValue ? <Icon size={icon.size} color={icon.color} /> : '';
  }

  if (typeof cellValue === 'boolean') {
    return cellValue ? <CheckIcon size={20} style={{ color: 'var(--cleos-positive)' }} /> : '';
  }

  if (textKey) return <FormattedMessage id={`generalValues.${cellValue}`} />;

  if (checkbox) {
    const { isDisabled, isChecked, onChange } = checkbox;
    const typedRow = row as { id?: unknown };
    return (
      <span
        onMouseDown={(e) => {
          if (e.shiftKey && !isDisabled(row)) {
            onChange(row, !isChecked(row));
          }
        }}
      >
        <Checkbox
          data-testid="row-checkbox"
          key={`${typedRow.id}${isChecked(row)}`}
          checked={isChecked(row)}
          disabled={isDisabled(row)}
          onChange={(e) => onChange(row, e.target.checked)}
          style={{ margin: '0 auto' }}
        />
      </span>
    );
  }

  if (isRef) {
    if (typeof cellValue === 'object' && cellValue !== null) {
      const refObj = cellValue as { id?: unknown; name?: string };
      if (refObj.name) {
        return (
          <Tooltip placement="top-left" content={String(refObj.id)}>
            <span className={styles.nameValue}>
              <span className={styles.hide}>
                <CopyToClipboard text={String(refObj.id)} />
              </span>
              {refObj.name}
            </span>
          </Tooltip>
        );
      }
      return (
        <span className={styles.nameValue}>
          <span className={styles.hide}>
            <CopyToClipboard text={String(refObj.id)} />
          </span>
          {String(refObj.id)}
        </span>
      );
    }
  }

  return cellValue as ReactNode;
};

export const isAllRowsChecked = (
  rows: unknown[],
  isChecked: ((row: unknown) => boolean) | undefined
): boolean | 'indeterminate' => {
  if (!isChecked) return false;
  const checkedCount = rows.filter((row) => isChecked(row)).length;
  if (checkedCount === 0) return false;
  if (checkedCount === rows.length) return true;
  return 'indeterminate';
};
